import { memo, useMemo } from "react";
import { Link } from "react-router";

import type { PlanList_DesignPageQuery } from "@/lib/gql/__generated__/graphql";

import { BlankContentForList } from "@/components/features/_components";
import { FeatureIslandContainer, FeatureIslandMainWithBackGround } from "@/components/features/_layouts";

import OpenInNew from "@/components/ui/icon/open-in-new";

import { DesignMagazineHeaderAndTab } from "../../../_shared/ui/components/header-and-tab";
import { DesignMagazinePlanListItem } from "../../../_shared/ui/components/list/list-item";
import { useInitialized } from "../../model/use-case";

type Props = {
	serverModel: PlanList_DesignPageQuery;
	hotelID: string;
};

const PLAN_MAGAZINE_WHERE_TO_DISPLAY_DESCRIPTION_LINK = "https://chillnn.notion.site/86f55498f1c54d98bb234982ea3f6d48";

export const DesignMagazinePlanListPresenter = memo(({ hotelID, serverModel }: Props) => {
	const { data } = useInitialized({ serverModel });

	/**
	 * 売り出し中のプランリスト
	 */
	const inSalePlanList = useMemo(() => data.planList.plans.filter((plan) => plan.inSale), [data.planList.plans]);
	/**
	 * 売り出し中のプランリストを表示できるか
	 */
	const canShowInSalePlanList = useMemo(() => inSalePlanList.length > 0, [inSalePlanList]);

	/**
	 * シークレットプランリストを表示できるか
	 */
	const canShowSecretPlanList = useMemo(() => data.planList.secrets.length > 0, [data.planList.secrets]);

	/**
	 * 売り止め中のプランリスト
	 */
	const notInSalePlanList = useMemo(
		() => [...data.planList.plans, ...data.planList.secrets].filter((plan) => !plan.inSale),
		[data.planList.plans, data.planList.secrets],
	);
	/**
	 * 売り止め中のプランリストを表示できるか
	 */
	const canShowNotInSalePlanList = useMemo(() => notInSalePlanList.length > 0, [notInSalePlanList]);

	return (
		<FeatureIslandContainer>
			<DesignMagazineHeaderAndTab hotelID={hotelID} />

			<FeatureIslandMainWithBackGround islandTitle="売り出し中">
				<div className="flex flex-wrap gap-x-1 gap-y-2">
					<p>マガジン版 「プラン」の欄に表示される記事を編集できます。</p>

					<div className="flex items-center gap-x-0.5">
						<p>（</p>
						<Link
							to={PLAN_MAGAZINE_WHERE_TO_DISPLAY_DESCRIPTION_LINK}
							target="_blank"
							className="flex items-center gap-x-1 text-link-400 underline-offset-4 hover:underline"
						>
							表示場所に関する詳細はこちら
							<OpenInNew size="sm" />
						</Link>
						<p>）</p>
					</div>
				</div>

				{canShowInSalePlanList ? (
					<>
						{inSalePlanList.map((item) => {
							return (
								<DesignMagazinePlanListItem
									key={item.id}
									searchParams={{ resourceID: item.planID, resourceType: "Plan" }}
									hotelID={hotelID}
									id={item.id}
									mainKeyVisual={item.mainKeyVisual}
									name={item.name}
								/>
							);
						})}
					</>
				) : (
					<BlankContentForList content="売り出し中のプラン記事はありません。" />
				)}
			</FeatureIslandMainWithBackGround>

			<FeatureIslandMainWithBackGround islandTitle="シークレットプラン">
				{canShowSecretPlanList ? (
					<>
						{data.planList.secrets.map((item) => {
							return (
								<DesignMagazinePlanListItem
									searchParams={{ resourceID: item.planID, resourceType: "Plan" }}
									hotelID={hotelID}
									key={item.id}
									id={item.id}
									mainKeyVisual={item.mainKeyVisual}
									name={item.name}
								/>
							);
						})}
					</>
				) : (
					<BlankContentForList content="シークレットプランの記事はありません。" />
				)}
			</FeatureIslandMainWithBackGround>

			<FeatureIslandMainWithBackGround islandTitle="売り止め中">
				{canShowNotInSalePlanList ? (
					<>
						{notInSalePlanList.map((item) => {
							return (
								<DesignMagazinePlanListItem
									searchParams={{ resourceID: item.planID, resourceType: "Plan" }}
									hotelID={hotelID}
									key={item.id}
									id={item.id}
									mainKeyVisual={item.mainKeyVisual}
									name={item.name}
								/>
							);
						})}
					</>
				) : (
					<BlankContentForList content="売り止め中のプラン記事はありません。" />
				)}
			</FeatureIslandMainWithBackGround>
		</FeatureIslandContainer>
	);
});

DesignMagazinePlanListPresenter.displayName = "DesignMagazinePlanList";
